import type { Channel, ChannelType } from "sdk";
import { type Component, For, Match, Show, Switch } from "solid-js";
import { Dynamic } from "solid-js/web";
import { useApi } from "@/api";
import {
	Info,
	Invites,
	Permissions,
	Tags,
	Webhooks,
} from "@/components/features/channel_settings/mod";
import { useCurrentUser } from "@/contexts/currentUser";
import { useModals } from "@/contexts/modal";
import { useNavigate } from "@/contexts/router";
import { usePermissions } from "@/hooks/usePermissions";

type PermissionCheck = (p: ReturnType<typeof usePermissions>) => boolean;

const tabs: Array<{
	name: string;
	path: string;
	noPad?: boolean;
	component: Component<{ channel: Channel }> | null;
	action?: "remove";
	style?: string;
	permissionCheck?: PermissionCheck;
	channelTypes?: ChannelType[];
}> = [
	{ name: "info", path: "", component: Info },
	{
		name: "invites",
		path: "invites",
		component: Invites,
		// TODO: what channelTypes are sensible here?
		permissionCheck: (p) => p.has("InviteManage"),
	},
	{
		name: "permissions",
		path: "permissions",
		component: Permissions,
		noPad: true,
		permissionCheck: (p) => p.has("RoleManage"),
	},
	{
		name: "tags",
		path: "tags",
		component: Tags,
		permissionCheck: (p) => p.has("RoleManage"),
		channelTypes: ["Forum", "Forum2"],
		// permissionCheck: (p) => p.has("RoleManage") || p.has("ChannelManage"),
	},
	{
		name: "webhooks",
		path: "webhooks",
		component: Webhooks,
		channelTypes: ["Text", "Announcement"],
		// TODO: do i allow webhooks in forums? ["Forum", "Forum2", "Ticket"]
		// TODO: do i allow webhooks in voice channels (that have text)? ["Voice", "Broadcast"]
		// TODO: do i allow webhooks in threads? ["ThreadPublic", "ThreadPrivate", "ThreadForum2"]
		// i'll probably require webhooks to be created on top level channels, use ?thread_id or similar (copied from disccord) to send messages in threads
		// TODO: do i allow webhooks in gdms? ["Gdm"]
		permissionCheck: (p) => p.has("IntegrationsManage"),
	},
	{
		name: "remove channel",
		path: "remove",
		component: null,
		action: "remove",
		style: "danger",
		// TODO: check ThreadManage in threads, ChannelManage in channels
		permissionCheck: (p) => p.has("ThreadManage") || p.has("ChannelManage"),
	},
];

export const ChannelSettings = (props: { channel: Channel; page: string }) => {
	const api2 = useApi();
	const [, modalCtl] = useModals();
	const nav = useNavigate();
	const currentUser = useCurrentUser();
	const user_id = () => currentUser()?.id;
	const perms = usePermissions(
		user_id,
		() => props.channel.room_id ?? undefined,
		() => props.channel.id,
	);

	const currentTab = () => tabs.find((i) => i.path === (props.page ?? ""));

	const handleAction = (action: string) => {
		switch (action) {
			case "remove":
				modalCtl.confirm(
					`Are you sure you want to remove "${props.channel.name}"?`,
					(confirmed) => {
						if (confirmed) {
							api2.client.http
								.DELETE("/api/v1/channel/{channel_id}/remove", {
									params: { path: { channel_id: props.channel.id } },
								})
								.then(() => {
									// assuming channel has room_id
									const parentRoomId = props.channel.room_id;
									window.location.href = `/room/${parentRoomId}`;
								})
								.catch((error) => {
									console.error("Failed to remove channel:", error);
									modalCtl.alert(`Failed to remove channel: ${error.message}`);
								});
						}
					},
				);
				break;
			default:
				console.warn(`Unknown action: ${action}`);
		}
	};

	return (
		<div class="settings">
			<header>
				channel settings: {currentTab()?.name}{" "}
				<a href={`/channel/${props.channel.id}`}>back</a>
			</header>
			<nav>
				<ul>
					<For each={tabs}>
						{(tab) => (
							<Show
								when={
									(!tab.channelTypes ||
										tab.channelTypes.includes(props.channel.type)) &&
									(!tab.permissionCheck || tab.permissionCheck(perms))
								}
							>
								<Switch>
									<Match when={tab.action}>
										{(action) => (
											<li>
												<button
													type="button"
													class="action"
													onClick={() => handleAction(action())}
													classList={{
														danger: tab.style === "danger",
													}}
												>
													{tab.name}
												</button>
											</li>
										)}
									</Match>
									<Match when={true}>
										<li>
											<a
												href={`/channel/${props.channel.id}/settings/${tab.path}`}
												onClick={(e) => {
													e.preventDefault();
													nav(
														`/channel/${props.channel.id}/settings/${tab.path}`,
													);
												}}
											>
												{tab.name}
											</a>
										</li>
									</Match>
								</Switch>
							</Show>
						)}
					</For>
				</ul>
			</nav>
			<main classList={{ padded: !currentTab()?.noPad }}>
				<Show when={currentTab()?.component} fallback="unknown page">
					{(component) => (
						<Dynamic component={component()} channel={props.channel} />
					)}
				</Show>
			</main>
		</div>
	);
};
